import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const ReviewsList = ({ travelerId, currentLanguage }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const translations = {
    en: {
      title: 'Recent Reviews',
      noReviews: 'No reviews yet',
      loadMore: 'Load More',
      delivered: 'Delivered'
    },
    ko: {
      title: '최근 리뷰',
      noReviews: '아직 리뷰가 없습니다',
      loadMore: '더 보기',
      delivered: '배송 완료'
    }
  };

  const t = translations?.[currentLanguage];

  useEffect(() => {
    loadReviews();
  }, [travelerId]);

  const loadReviews = async () => {
    try {
      setLoading(true);
      // Load completed deals with buyer info
      const { data, error } = await supabase
        ?.from('deals')
        ?.select(`
          id,
          status,
          created_at,
          buyer:buyer_id (
            full_name,
            avatar_url
          ),
          request:request_id (
            item_name,
            category
          )
        `)
        ?.eq('traveler_id', travelerId)
        ?.eq('status', 'completed')
        ?.order('created_at', { ascending: false })
        ?.limit(5);

      if (error && error?.code !== 'PGRST116') {
        console.error('Load reviews error:', error);
      }

      setReviews(data || []);
    } catch (error) {
      console.error('Load reviews error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return currentLanguage === 'en' ? date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : date?.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars?.push(
        <Icon
          key={i}
          name="Star"
          size={14}
          color="var(--color-warning)"
          fill={i < rating ? 'var(--color-warning)' : 'transparent'}
        />
      );
    }
    return stars;
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg shadow-elevation-md overflow-hidden h-full">
        <div className="p-4 md:p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="MessageSquare" size={20} color="var(--color-primary)" />
            </div>
            <h2 className="text-xl font-heading font-semibold text-foreground">
              {t?.title}
            </h2>
          </div>
        </div>
        <div className="p-6 flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-elevation-md overflow-hidden h-full flex flex-col">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="MessageSquare" size={20} color="var(--color-primary)" />
          </div>
          <h2 className="text-xl font-heading font-semibold text-foreground">
            {t?.title}
          </h2>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {reviews?.length === 0 ? (
          <div className="p-6 text-center">
            <Icon name="MessageSquare" size={48} color="var(--color-muted)" className="mx-auto mb-3" />
            <p className="text-muted-foreground">{t?.noReviews}</p>
          </div>
        ) : (
          <div className="p-4 md:p-6 space-y-4">
            {reviews?.map((review) => (
              <div key={review?.id} className="p-4 bg-muted/20 rounded-lg">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="User" size={20} color="var(--color-primary)" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-foreground">
                        {review?.buyer?.full_name || 'Anonymous Buyer'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(review?.created_at)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 mb-2">
                      {renderStars(5)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Icon name="Package" size={14} color="var(--color-muted-foreground)" />
                  <span className="text-muted-foreground">
                    {review?.request?.item_name} • {review?.request?.category}
                  </span>
                </div>
                <div className="mt-2 flex items-center gap-1.5 text-xs text-success">
                  <Icon name="CheckCircle" size={12} color="var(--color-success)" />
                  <span>{t?.delivered}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewsList;